import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router";
import { Loading, useRedirect } from "react-admin";
import { DatePicker } from "@mui/x-date-pickers";

import { commonStyle, Wrapper } from "../../CommonStyles";
import RouteLeavingGuard from "../../components/RouteLeavingGuard/RouteLeavingGuard";
import { exhibitorEdit as classes } from "./styles";

import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import FdButton from "../../components/UIComponents/Button/FdButton";
import FdIconButton from "../../components/UIComponents/IconButton/FdIconButton";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
import FdSelect from "../../components/UIComponents/Select/FdSelect";

import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";

import { APIContext } from "../../store/APIStore";
import { StoreContext } from "../../store/Store";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";

import { IExhibitor } from "../../dataProvider/interfaces/ILeads";
import Typography from "@mui/material/Typography";
import Resend from "../../Svg/Resend";
import { routePaths } from "../../baseLayout/SidebarMenu";
import { RiGroupLine } from "react-icons/ri";
import { theme } from "../../theme";
import moment from "moment";

const ExhibitorEdit = () => {
  const { exhibitorId, eventId } = useParams() as any;
  const [state, dispatch] = useContext(StoreContext);
  const api = useContext(APIContext);
  const notificationRef = useRef<any>();
  const navigate = useNavigate();
  const redirect = useRedirect();
  const [loading, setLoading] = useState(false);
  const [currentExhibitor, setCurrentExhibitor] = useState<IExhibitor>();

  const initialValues: { [key: string]: any } = {
    name: currentExhibitor?.name,
    userName: currentExhibitor?.username,
    email: currentExhibitor?.email,
    contactNo: currentExhibitor?.organisation?.contact,
    city: currentExhibitor?.organisation?.city,
    country: currentExhibitor?.organisation?.country,
    address: currentExhibitor?.organisation?.address,
    org: currentExhibitor?.organisation?.name,
    orderId: currentExhibitor?.licenseDetails?.orderId,
    deviceCount: currentExhibitor?.licenseDetails?.deviceCount || 0,
    licenseCount: currentExhibitor?.licenseDetails?.licenseCount || 0,
    licenseType: currentExhibitor?.licenseDetails?.type,
    dateOfPurchase:
      currentExhibitor?.licenseDetails?.dateOfPurchase &&
      new Date(currentExhibitor?.licenseDetails?.dateOfPurchase),
  };
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { isDirty, errors },
  } = useForm({
    defaultValues: initialValues,
    mode: "onBlur",
  });
  useEffect(() => {
    reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExhibitor, reset]);

  useEffect(() => {
    // fetch the selected event if it wasn't set in the store
    if (eventId && !state.selectedEvent) {
      fetchSelectedEvent();
    }
    if (exhibitorId) {
      fetchExhibitor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disablePast = true;

  const fetchSelectedEvent = async () => {
    try {
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data?.message,
        notificationType.Error
      );
    }
  };
  const fetchExhibitor = async () => {
    try {
      setLoading(true);
      const exhibitor = await api.leadsService.getExhibitorDetail(exhibitorId);
      setCurrentExhibitor(exhibitor.data);
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      notificationRef.current?.show(
        error.response?.data.message,
        notificationType.Error
      );
    }
  };

  const createExhibitor = async (values: any) => {
    try {
      const newExhibitor = (await api.leadsService.createExhibitor(
        values
      )) as any;
      setCurrentExhibitor(newExhibitor.data);
      notificationRef.current?.show(
        newExhibitor?.message,
        notificationType.Success
      );
      navigate(-1);
    } catch (error: any) {
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };
  const updateExhibitor = async (values: any) => {
    try {
      const updatedExhibitor = (await api.leadsService.updateExhibitor(
        exhibitorId,
        { ...values }
      )) as any;
      setCurrentExhibitor(updatedExhibitor.data);
      notificationRef.current?.show(
        updatedExhibitor.message,
        notificationType.Success
      );
    } catch (error: any) {
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const onSubmit = async (values: any) => {
    const exhibitorPayload = {
      eventId: Number(eventId),
      name: values.name.trim(),
      password: values.password,
      email: values.email.trim(),
      contact: values.contactNo.trim(),
      city: values.city.trim(),
      country: values.country.trim(),
      address: values.address.trim(),
      organisation: values.org.trim(),
      username: values.userName,
      licenseDetails: {
        orderId: values.orderId,
        deviceCount:
          String(values.licenseType).toUpperCase() === "LICENSE_SCANNER"
            ? values.deviceCount
            : 0,
        dateOfPurchase: values.dateOfPurchase
          ? new Date(values.dateOfPurchase).toISOString()
          : new Date().toISOString(),
        type: String(values.licenseType).toUpperCase().trim(),
        licenseCount: values.licenseCount,
      },
    };

    if (exhibitorId) {
      const { eventId, ...updatedPayload } = exhibitorPayload;
      updateExhibitor(updatedPayload);
    } else {
      const { username, password, ...createPayload } = exhibitorPayload;
      createExhibitor(createPayload);
    }
  };

  if (loading) {
    return <Loading />;
  }

  const handleResendCreds = async () => {
    try {
      const resend = (await api.leadsService.resendCredential(
        exhibitorId
      )) as any;
      if (resend.status === 200) {
        notificationRef.current?.show(
          resend?.message,
          notificationType.Success
        );
      }
    } catch (error: any) {
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };
  return (
    <>
      <Box sx={classes.wrapper}>
        <Wrapper sx={{ width: "100%" }}>
          <Box sx={classes.topActions} aria-label="topActions">
            <Box sx={classes.infoTitle}>
              <Box textAlign="left">
                <FdTypography
                  label={exhibitorId ? currentExhibitor?.name : "Add exhibitor"}
                  variant="h2"
                  component="h2"
                  style={{ marginBottom: "10px" }}
                />
                {exhibitorId && (
                  <Box sx={{ display: "flex" }}>
                    <FdTypography
                      sx={{ paddingRight: "12px" }}
                      label={`App Login: ${
                        currentExhibitor?.appLoggedInAt
                          ? moment(currentExhibitor.appLoggedInAt).format(
                              "MMMM DD, YYYY HH:mm"
                            )
                          : "No"
                      }`}
                    />
                    <FdTypography
                      label={`Web Login: ${
                        currentExhibitor?.webLoggedInAt
                          ? moment(currentExhibitor.webLoggedInAt).format(
                              "MMMM DD, YYYY HH:mm"
                            )
                          : "No"
                      }`}
                    />
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {exhibitorId && (
                <FdButton
                  startIcon={<Resend />}
                  ButtonName="Send reset password link"
                  variant="text"
                  onClick={handleResendCreds}
                />
              )}
              <FdButton
                variant="contained"
                color="primary"
                size="small"
                type="submit"
                form="eventForm"
                disabled={!isDirty}
              >
                Save
              </FdButton>

              <FdIconButton
                aria-label="close"
                onClick={() => {
                  redirect(
                    `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.EXHIBITOR}`
                  );
                }}
                size="large"
                Icon={<CloseIcon />}
              />
            </Box>
          </Box>

          <Box sx={classes.formContent} className="formContent">
            <form
              id="eventForm"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              style={{
                width: "100%",
                textAlign: "left",
                display: "flex",
                gap: "20px",
              }}
            >
              <Box sx={classes.genericFields} className="genericFields">
                <Box sx={classes.formTitle}>
                  <FdTypography
                    label="Exhibitor information"
                    variant="h2"
                    component="h2"
                  />
                </Box>
                <Box sx={classes.formFields}>
                  <Box sx={{ mb: 2 }}>
                    <FdTextField
                      register={register}
                      label="Name"
                      name="name"
                      required
                      error={Boolean(errors.name)}
                      errorMsg={errors.name && "Name is required"}
                    />
                  </Box>

                  {exhibitorId && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <FdTextField
                          autoComplete="off"
                          register={register}
                          label="Username"
                          name="userName"
                          required
                          error={Boolean(errors.userName)}
                          errorMsg={errors.userName && "Username is required"}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <FdTextField
                          autoComplete="off"
                          register={register}
                          type="password"
                          label="Password"
                          name="password"
                        />
                      </Box>
                    </>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <FdTextField
                      register={register}
                      label="Email"
                      name="email"
                      required
                      error={Boolean(errors.email)}
                      errorMsg={errors.org && "Email is required"}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <FdTextField
                      register={register}
                      label="Contact number"
                      name="contactNo"
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <FdTextField
                      register={register}
                      label="Organization"
                      name="org"
                      required
                      error={Boolean(errors.org)}
                      errorMsg={errors.org && "Organization is required"}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <FdTextField
                      register={register}
                      label="Address"
                      name="address"
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <FdTextField register={register} label="City" name="city" />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <FdTextField
                      register={register}
                      label="Country"
                      name="country"
                    />
                  </Box>
                </Box>
              </Box>
              {/* licence */}
              <Box
                sx={{ ...classes.genericFields, width: "50%" }}
                className="genericFields"
              >
                <Box sx={classes.formTitle}>
                  <FdTypography
                    label="License details"
                    variant="h2"
                    component="h2"
                  />
                </Box>
                <Box sx={classes.formFields}>
                  <Box sx={{ mb: 2 }}>
                    <FdTextField
                      register={register}
                      label="order ID"
                      name="orderId"
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Controller
                      {...register("dateOfPurchase")}
                      control={control}
                      defaultValue={initialValues.dateOfPurchase || new Date()}
                      render={({
                        field: { onBlur, onChange, value, name, ...field },
                        fieldState: { error },
                      }) => (
                        <>
                          <DatePicker
                            label="Date of purchase"
                            {...field}
                            value={value}
                            onChange={(newValue) => {
                              onChange(newValue); // Update the form value
                            }}
                            format="yyyy.MM.dd"
                            disablePast={disablePast}
                            sx={commonStyle.calendarStyle}
                            className={disablePast ? "noPastErrorStyle" : ""}
                            slotProps={{
                              textField: {
                                helperText: error ? error.message : "",
                                error: Boolean(error),
                              },
                            }}
                          />
                        </>
                      )}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Controller
                      name="licenseCount"
                      control={control}
                      defaultValue={initialValues?.licenseCount}
                      rules={{
                        min: {
                          value: 0,
                          message: "License count must not be less than 0",
                        },
                      }}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <Box sx={{ mb: 2 }}>
                          <FdTextField
                            type="number"
                            label="License count"
                            value={value}
                            onFocus={(e) => e.target.select()} // Select the text on focus
                            onChange={onChange}
                            error={Boolean(error)}
                            errorMsg={error ? error.message : ""}
                          />
                        </Box>
                      )}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Controller
                      {...register("licenseType")}
                      control={control}
                      defaultValue={initialValues.licenseType}
                      rules={{
                        required: "License type is required",
                      }}
                      render={({ field, fieldState: { error } }) => {
                        return (
                          <>
                            <FdSelect
                              {...field}
                              required
                              name="licenseType"
                              onChange={field.onChange}
                              label="License type"
                              error={Boolean(error)}
                              helperText={error ? error.message : ""}
                            >
                              <MenuItem value="LICENSE">License only</MenuItem>
                              <MenuItem value="LICENSE_SCANNER">
                                License + scanner
                              </MenuItem>
                            </FdSelect>
                            {error && ( // Conditionally render the error message
                              <Typography variant="caption" color="error">
                                {error.message}
                              </Typography>
                            )}
                          </>
                        );
                      }}
                    />
                  </Box>
                  {watch("licenseType") === "LICENSE_SCANNER" ? (
                    <Box sx={{ mb: 2 }}>
                      <Controller
                        name="deviceCount"
                        control={control}
                        defaultValue={initialValues?.deviceCount}
                        rules={{
                          min: {
                            value: 0,
                            message: "Device count must not be less than 0",
                          },
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <Box sx={{ mb: 2 }}>
                            <FdTextField
                              type="number"
                              label="Device count"
                              value={value}
                              onFocus={(e) => e.target.select()} // Select the text on focus
                              onChange={onChange}
                              error={Boolean(error)}
                              errorMsg={error ? error.message : ""}
                            />
                          </Box>
                        )}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Box>
              <NotificationMessage ref={notificationRef} />

              <RouteLeavingGuard
                when={isDirty}
                navigate={(path) => navigate(path)}
                shouldBlockNavigation={() => isDirty}
              />
            </form>
          </Box>
        </Wrapper>
      </Box>
    </>
  );
};

export default ExhibitorEdit;
